// Confirmation modal built on the native <dialog> element so we get
// focus trap, ESC handling, and a backdrop for free without pulling
// in another dependency. Used to replace window.confirm() and
// window.prompt() across the team portal.
//
// Two shapes:
//   - Plain confirm: pass title + body + confirm/cancel labels.
//   - Confirm + reason input: pass `reason` props to capture a free-text
//     justification (replaces window.prompt for the abandon flow).
//
// Open/close is parent-controlled so the caller can carry their own
// busy state. The dialog itself doesn't fire anything until the user
// presses confirm (or cancel/ESC).

import { useEffect, useRef, useState } from "react";
import { FieldErrorText } from "./application/FormField";

interface BaseProps {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onCancel: () => void;
}

interface PlainConfirmProps extends BaseProps {
  reason?: undefined;
  onConfirm: () => void;
}

interface ReasonConfirmProps extends BaseProps {
  reason: {
    label: string;
    placeholder?: string;
    minLength?: number;
    maxLength?: number;
  };
  onConfirm: (reason: string) => void;
}

type Props = PlainConfirmProps | ReasonConfirmProps;

export function ConfirmDialog(props: Props) {
  const {
    open,
    title,
    body,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = false,
    busy = false,
    onCancel,
  } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  // Sync the open prop with the native dialog. We always use showModal()
  // (not show()) so focus is trapped and the backdrop is rendered.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      setReasonText("");
      setReasonError(null);
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Native dialog fires `cancel` on ESC; surface that to the parent so
  // their busy state stays in sync.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const handler = (e: Event) => {
      e.preventDefault();
      if (!busy) onCancel();
    };
    dlg.addEventListener("cancel", handler);
    return () => dlg.removeEventListener("cancel", handler);
  }, [onCancel, busy]);

  function handleConfirm() {
    if (busy) return;
    if (props.reason) {
      const trimmed = reasonText.trim();
      const min = props.reason.minLength ?? 0;
      if (trimmed.length < min) {
        setReasonError(
          min === 1
            ? `${props.reason.label} is required.`
            : `${props.reason.label} must be at least ${min} characters.`,
        );
        return;
      }
      props.onConfirm(trimmed);
    } else {
      props.onConfirm();
    }
  }

  // Backdrop click closes the dialog. The native <dialog> doesn't do
  // this out of the box; this is the canonical workaround (treat
  // clicks on the dialog itself, where target === currentTarget,
  // as backdrop clicks).
  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (busy) return;
    if (e.target === e.currentTarget) onCancel();
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      aria-labelledby="confirm-dialog-title"
      className="m-auto w-full max-w-md rounded-xl border border-brand-border bg-brand-surface p-0 text-brand-text shadow-pop backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="px-6 py-5">
        <h2
          id="confirm-dialog-title"
          className="text-lg font-semibold tracking-tight text-white"
        >
          {title}
        </h2>
        {body && <div className="mt-2 text-sm leading-6 text-brand-muted">{body}</div>}
        {props.reason && (
          <div className="mt-4">
            <label
              htmlFor="confirm-dialog-reason"
              className="label mb-1.5 block"
            >
              {props.reason.label}
            </label>
            <textarea
              id="confirm-dialog-reason"
              autoFocus
              value={reasonText}
              onChange={(e) => {
                setReasonText(e.target.value);
                if (reasonError) setReasonError(null);
              }}
              placeholder={props.reason.placeholder}
              maxLength={props.reason.maxLength ?? 500}
              className="input min-h-[88px] resize-y text-sm"
              aria-invalid={reasonError ? true : undefined}
            />
            {reasonError && <FieldErrorText>{reasonError}</FieldErrorText>}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-brand-border bg-brand-bg/40 px-6 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="btn-secondary py-2"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          className={
            destructive
              ? "inline-flex items-center justify-center rounded-lg bg-gradient-to-b from-[#F26B6B] to-[#DC4B4B] px-4 py-2 font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_1px_2px_rgba(0,0,0,0.4)] transition-all duration-150 hover:from-[#F57C7C] hover:to-[#E25555] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-danger/35 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
              : "btn-primary py-2"
          }
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
